import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/stores/authStore';
import { useCreateTransfer, useAccounts } from '@/hooks/queries/useTreasury';
import { toast } from '@/components/ui/toast/useToast';
import { Loader2, ArrowRight, AlertCircle } from 'lucide-react';

const transferSchema = z.object({
  from_account_id: z.string().min(1, 'Compte source requis'),
  to_account_id: z.string().min(1, 'Compte destination requis'),
  amount: z.number().min(0.01, 'Montant requis'),
  transfer_fee: z.number().min(0, 'Frais invalides').default(0),
  transfer_date: z.string(),
  description: z.string().min(1, 'Description requise'),
  notes: z.string().optional(),
}).refine((data) => data.from_account_id !== data.to_account_id, {
  message: 'Les comptes source et destination doivent être différents',
  path: ['to_account_id'],
});

type TransferFormData = z.infer<typeof transferSchema>;

interface TransferFormProps {
  open: boolean;
  onClose: () => void;
}

export function TransferForm({ open, onClose }: TransferFormProps) {
  const { company, user } = useAuthStore();
  const { data: accounts = [] } = useAccounts(company?.id || '');
  const createTransfer = useCreateTransfer();

  const form = useForm<TransferFormData>({
    resolver: zodResolver(transferSchema),
    defaultValues: {
      transfer_date: new Date().toISOString().split('T')[0],
      amount: 0,
      transfer_fee: 0,
    },
  });

  const fromAccountId = form.watch('from_account_id');
  const toAccountId = form.watch('to_account_id');
  const amount = form.watch('amount');
  const transferFee = form.watch('transfer_fee');

  // Get account details
  const fromAccount = accounts.find((a) => a.id === fromAccountId);
  const toAccount = accounts.find((a) => a.id === toAccountId);

  // Calculate total deduction
  const totalDeduction = amount + transferFee;

  // Check if from account has sufficient balance
  const hasSufficientBalance = fromAccount ? fromAccount.current_balance >= totalDeduction : true;

  // Generate transfer number
  const generateTransferNumber = () => {
    const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `TRF-${date}-${random}`;
  };

  const onSubmit = async (data: TransferFormData) => {
    try {
      // Validate balance
      if (!hasSufficientBalance) {
        toast.error('Solde insuffisant', 'Le compte source n\'a pas assez de fonds.');
        return;
      }

      await createTransfer.mutateAsync({
        ...data,
        company_id: company?.id,
        transfer_number: generateTransferNumber(),
        created_by: user?.id,
        status: 'completed',
      });

      toast.success('Transfert effectué', 'Le transfert a été effectué avec succès.');

      onClose();
      form.reset();
    } catch (error) {
      toast.error('Erreur', 'Une erreur est survenue lors du transfert.');
      console.error('Transfer error:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            تحويل بين الحسابات / Transfert entre comptes
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* From Account */}
            <FormField
              control={form.control}
              name="from_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>من الحساب / Depuis le compte *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez le compte source" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500">
                          Aucun compte disponible
                        </div>
                      ) : (
                        accounts
                          .filter((a) => a.id !== toAccountId) // Don't show selected destination
                          .map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              <div className="flex items-center justify-between gap-4">
                                <span>{account.name}</span>
                                <span className="text-sm text-gray-500">
                                  {formatCurrency(account.current_balance)}
                                </span>
                              </div>
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  {fromAccount && (
                    <p className="text-sm text-gray-600 mt-1">
                      Solde disponible: {formatCurrency(fromAccount.current_balance)}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Visual Arrow */}
            {fromAccountId && toAccountId && (
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-700">{fromAccount?.name}</p>
                    <p className="text-xs text-gray-500">{formatCurrency(fromAccount?.current_balance || 0)}</p>
                  </div>
                  <ArrowRight className="w-8 h-8 text-emerald-600" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-700">{toAccount?.name}</p>
                    <p className="text-xs text-gray-500">{formatCurrency(toAccount?.current_balance || 0)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* To Account */}
            <FormField
              control={form.control}
              name="to_account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>إلى الحساب / Vers le compte *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez le compte destination" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts
                        .filter((a) => a.id !== fromAccountId) // Don't show selected source
                        .map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            <div className="flex items-center justify-between gap-4">
                              <span>{account.name}</span>
                              <span className="text-sm text-gray-500">
                                {formatCurrency(account.current_balance)}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {toAccount && (
                    <p className="text-sm text-gray-600 mt-1">
                      Solde actuel: {formatCurrency(toAccount.current_balance)}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* Amount */}
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>المبلغ / Montant (DA) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Transfer Fee */}
              <FormField
                control={form.control}
                name="transfer_fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>الرسوم / Frais (DA)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <p className="text-xs text-gray-500 mt-1">
                      Frais bancaires si applicable
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Date */}
            <FormField
              control={form.control}
              name="transfer_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>التاريخ / Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>الوصف / Description *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Transfert pour opération X" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ملاحظات / Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Notes supplémentaires..."
                      className="min-h-[60px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Balance Warning */}
            {fromAccountId && amount > 0 && !hasSufficientBalance && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-900">Solde insuffisant</p>
                    <p className="text-sm text-red-700">
                      Solde disponible: {formatCurrency(fromAccount?.current_balance || 0)}
                      <br />
                      Montant requis: {formatCurrency(totalDeduction)} (dont {formatCurrency(transferFee)} de frais)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Transfer Summary */}
            {fromAccountId && toAccountId && amount > 0 && hasSufficientBalance && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <h4 className="font-medium text-emerald-900 mb-2">Résumé du transfert</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Montant à transférer:</span>
                    <span className="font-medium">{formatCurrency(amount)}</span>
                  </div>
                  {transferFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-700">Frais de transfert:</span>
                      <span className="font-medium">{formatCurrency(transferFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-emerald-200">
                    <span className="font-medium text-gray-900">Total déduit de {fromAccount?.name}:</span>
                    <span className="font-bold text-red-600">-{formatCurrency(totalDeduction)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">Ajouté à {toAccount?.name}:</span>
                    <span className="font-bold text-emerald-600">+{formatCurrency(amount)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createTransfer.isPending}
              >
                إلغاء / Annuler
              </Button>
              <Button
                type="submit"
                disabled={createTransfer.isPending || !hasSufficientBalance}
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
              >
                {createTransfer.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                تحويل / Transférer
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
